import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp } = FiIcons;

const excerpts = [
  {
    chapter: "Chapter 1",
    title: "The Language No One Taught You to Read",
    text: `We grew up watching love arrive in grand packages. The bouquet of roses delivered to the door. The airport run where he catches her just before she boards. The rooftop dinner with candles and a ring. These images taught us that romance is an event — something that happens to you, something you can photograph and show the world.\n\nBut what if the most romantic thing he ever does is simply stay? What if it is the coffee made exactly as you like it, without a word said? The way he slows the car when you are nervous, or sits beside you in silence when the world has been too loud?\n\nThis book is an invitation to look again. To see what has always been there, in the spaces between the grand gestures.`
  },
  {
    chapter: "Chapter 3",
    title: "He Fixes Things You Did Not Ask Him To",
    text: `There is a particular kind of love that expresses itself through tools and effort. He notices the drawer that sticks. He re-hangs the frame that has been slightly crooked for months. He does not announce it. He does not wait for applause.\n\nThis is not practicality. It is attention. It is the quiet accumulation of care — the proof that he is paying attention to your world, and that he considers it his responsibility to make it better.\n\nWhen a man fixes things in your life without being asked, he is saying something he may never say out loud: your comfort matters to me. Your peace is my project. I am here.`
  },
  {
    chapter: "Chapter 6",
    title: "Presence Is the Most Underrated Gift",
    text: `He does not always know what to say. And sometimes, he says nothing at all. He sits beside you. He stays. In a world that rewards busyness and performance, the choice to simply be present — unhurried, undistracted, fully there — is an act of profound generosity.\n\nWe have been taught to want words. We want to hear "I love you" spoken like a declaration. But there is a man who shows you every single day, in the way he does not leave when things are hard, in the way he turns toward you instead of away.\n\nHis silence is not absence. It is a different kind of language. And once you learn to read it, you will never feel unloved again.`
  }
];

const Excerpt = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-brand-600 font-semibold tracking-widest uppercase text-sm mb-4 block">
            Read Inside the Book
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-4">
            Sample Chapters
          </h2>
          <p className="text-lg text-stone-600">
            A glimpse into the writing style and emotional depth of the book.
          </p>
        </motion.div>

        <div className="space-y-4">
          {excerpts.map((excerpt, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-2xl border border-stone-100 overflow-hidden shadow-sm"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-8 py-6 flex justify-between items-center text-left hover:bg-stone-50 transition-colors"
              >
                <div>
                  <span className="text-xs font-bold tracking-widest uppercase text-brand-500 block mb-1">
                    {excerpt.chapter}
                  </span>
                  <h3 className="text-lg md:text-xl font-serif font-bold text-stone-900">
                    {excerpt.title}
                  </h3>
                </div>
                <SafeIcon
                  icon={openIndex === i ? FiChevronUp : FiChevronDown}
                  className="w-5 h-5 text-stone-400 flex-shrink-0 ml-4"
                />
              </button>

              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-8 border-t border-stone-100">
                      <div className="pt-6 space-y-4">
                        {excerpt.text.split('\n\n').map((para, j) => (
                          <p key={j} className="text-stone-600 leading-relaxed text-lg font-serif italic">
                            {para}
                          </p>
                        ))}
                      </div>
                      <div className="mt-8 pt-6 border-t border-stone-100 flex items-center justify-between">
                        <p className="text-sm text-stone-400">
                          — From <em>The Silent Language of Love</em> by Andrei Marius Ciomîrtan
                        </p>
                        <button
                          onClick={() => document.getElementById('get-copy').scrollIntoView({ behavior: 'smooth' })}
                          className="text-sm font-semibold text-brand-600 hover:text-brand-800 transition-colors"
                        >
                          Read the full book →
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Excerpt;